import { NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { PlaceDetail } from '../../../../services/locations.service';
import { UserService } from '../../../../services/user.service';
import { ErrorPart, apiErrorText } from '../workspace/api-errors';

/**
 * What the place panel and the map popup share (#253 step 3, Ivan 2026-10-16): who may take a provider out of
 * Conductor or open it in Service Providers, what each Conductor action asks and says, and how a place request runs.
 * Tested in place-actions.spec.ts.
 */

/** A place action's refusal reason is the useful part of its error. */
export const PLACE_ERROR_ORDER: ErrorPart[] = ['html', 'reason', 'message', 'thrown'];
const FAILED = 'something went wrong';

export type ConductorOutAction = 'remove' | 'retire' | 'unretire';

/** Remove from / restore to Conductor, stop using for new bookings: Content Managers and Operations. */
export function canManageConductor(users: UserService): boolean {
  const u = users.userSourceValue;
  return !!u && (u.isContentManager() || u.isOperation());
}

/** Links into Service Providers: not for content-only accounts, who don't see that section (Ivan, 2026-10-27). */
export function canOpenProviders(users: UserService): boolean {
  const u = users.userSourceValue;
  return !!u && !u.isContentManagerOnly();
}

/** What to ask before taking a provider out of Conductor (or back in for new bookings). */
export function conductorOutQuestion(
  action: ConductorOutAction,
  place: Pick<PlaceDetail, 'name' | 'provider' | 'providerRefs'>
): string {
  const name = place.provider?.name || place.name;
  if (action === 'remove')
    return `Remove "${name}" from Conductor? No arrangement uses it. It disappears from the provider lists (and from the website).`;
  if (action === 'retire') {
    const refs = place.providerRefs;
    return `Stop offering "${name}" for new bookings? The ${refs} arrangement${refs === 1 ? '' : 's'} using it keep it.`;
  }
  return `Offer "${name}" for new bookings again?`;
}

export const CONDUCTOR_OUT_DONE: Record<ConductorOutAction, string> = {
  remove: 'Removed from Conductor.',
  retire: 'No longer offered for new bookings.',
  unretire: 'Offered for new bookings again.',
};

export const RESTORED = 'Restored to Conductor.';

/** Mark a likely duplicate as "not this one", or take that back. */
export function toggleDismissed(dismissed: Set<number>, serviceId: number): void {
  if (dismissed.has(serviceId)) dismissed.delete(serviceId);
  else dismissed.add(serviceId);
}

/**
 * A place component's requests: busy while one runs, its failure as one line in `error`, and the answer handled back
 * in Angular's zone and redrawn (responses can land outside the zone in this app).
 */
export class PlaceRequests {
  busy = false;
  error = '';

  constructor(
    private zone: NgZone,
    private redraw: () => void
  ) {}

  run<T>(obs: Observable<T>, ok: (v: T) => void): void {
    this.busy = true;
    this.error = '';
    obs.pipe(take(1)).subscribe({
      next: (v) =>
        this.zone.run(() => {
          this.busy = false;
          ok(v);
          this.redraw();
        }),
      error: (e) =>
        this.zone.run(() => {
          this.busy = false;
          this.error = apiErrorText(e, FAILED, PLACE_ERROR_ORDER);
          this.redraw();
        }),
    });
  }
}
